<template>
  <page content-class="admin-campaign-list">
    <campaign-editor :campaign="campaign" step="participants">
      <div class="d-flex justify-content-center align-items-middle my-5" v-if="!campaign">
        <b-spinner variant="primary"/>
      </div>
      <div v-else class="p-3">
        <template v-if="state === null && hasFilters">
          <b-row class="my-3">
            <b-col cols="12" sm="6">
              <h4>Select Participants from Active Directory</h4>
              <p>When new employees are added to your organisation who fulfil the filter criteria, they will be automatically participating in the campaign.</p>
              <b-btn class="my-3" @click="() => state = 'CONFIGURE-SEGMENT'" variant="primary" block>Create a filter</b-btn>
            </b-col>
            <b-col cols="12" sm="6" class="border-left">
              <hr class="d-sm-none"/>
              <h4>Add Participants Manually</h4>
              <p>{{ $t('campaign-admin.hints.manual-selection') }}</p>
              <b-btn class="my-3" @click="() => state = 'UPLOAD-PARTICIPANTS'" variant="primary" block>Upload Participants</b-btn>
            </b-col>
          </b-row>
          <b-row class="border-top pt-3">
            <b-col>
              When none of the above options are selected, the campaign will be visible to all employees registered on the platform.
            </b-col>
          </b-row>
        </template>

        <update-scim-user-segment
          ref="updateScimUserSegmentComponent"
          v-if="state === 'CONFIGURE-SEGMENT' && hasFilters"
          :value="segment"
          @saveSegment="saveSegment"
          @back="state = null"
        />

        <update-imported-participants
          ref="updateImportedParticipantsComponent"
          v-if="state === 'UPLOAD-PARTICIPANTS' || !hasFilters"
          :value="invitedParticipants"
          @saveParticipants="saveParticipants"
          @back="state = null"
        />

      </div>
    </campaign-editor>

    <b-modal
      id="unsaved-changes-modal"
      title="Unsaved Changes"
      button-size="sm"
      @ok="() => { allowLeave = true; $router.push(leaveTo); }"
      cancel-title="No, go back"
      ok-title="Yes, leave"
      cancel-variant="primary"
      ok-variant="light"
    >
      <p class="my-2">You have some unsaved changes, are you sure you want to leave?</p>
    </b-modal>

  </page>
</template>

<script>
import Page from '@/components/campaign-admin/page';
import CampaignEditor from '@/components/campaign-admin/campaign-editor-frame';
import CampaignAdminParticipantsQuery from '@/graphql/queries/CampaignAdminParticipants.gql';
import UpdateScimUserSegment from '@/components/campaign-admin/update-scim-user-segment';
import UpdateImportedParticipants from '@/components/campaign-admin/update-imported-participants';
import UpdateCampaignMutation from '@/graphql/mutations/UpdateCampaign.gql';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'CampaignAdminParticipants',
  beforeRouteLeave(to, from, next) {
    const { allowLeave } = this;
    const { unsavedChanges } = this.$refs.updateScimUserSegmentComponent || this.$refs.updateImportedParticipantsComponent || {};
    console.log({ allowLeave, unsavedChanges });
    if (!unsavedChanges || allowLeave) {
      next();
    } else if (unsavedChanges) {
      this.leaveTo = {
        name: to.name,
        params: to.params,
        query: to.query
      };
      this.$bvModal.show('unsaved-changes-modal');
      next(false);
    } else {
      next();
    }
  },
  props: {
    campaignID: {
      type: String,
    },
  },
  components: {
    Page,
    CampaignEditor,
    UpdateScimUserSegment,
    UpdateImportedParticipants,
  },
  data: () => ({
    state: null,
    allowLeave: false,
    leaveTo: null
  }),
  apollo: {
    api: {
      query: CampaignAdminParticipantsQuery,
      variables() {
        const { campaignID } = this;
        return { campaignID };
      },
      update({ company }) {
        return company;
      },
      result({ data }) {
        const { company } = data || {};
        const { campaign } = company || {};
        const { segment } = campaign || {};
        const { invitedUsers } = campaign || {};
        const isSegment = segment && segment.scimUsersConnection &&
          segment.scimUsersConnection.where && segment.scimUsersConnection.where.length;
        this.state = isSegment ? 'CONFIGURE-SEGMENT' : null;
        const isInvitedParticipants = Array.isArray(invitedUsers) && invitedUsers.length;
        this.state = isInvitedParticipants ? 'UPLOAD-PARTICIPANTS' : this.state;
      },
      fetchPolicy: 'network-only'
    },
  },
  computed: {
    ...mapRoot([ 'isSimplifiedCampaign' ]),
    campaign() {
      const { campaign } = this.api || {};
      return campaign;
    },
    segment() {
      const { segment } = this.campaign || {};
      const result = segment && segment.scimUsersConnection && segment.scimUsersConnection.where ? segment.scimUsersConnection.where : null;
      return typeof result === 'string' ? JSON.parse(result) : null;
    },
    invitedParticipants() {
      const { invitedUsers } = this.campaign || {};
      return invitedUsers;
    },
    hasFilters() {
      const { api } = this;
      const { scimUserAggs } = api || {};
      const { locations, departments, organizations, divisions, titles, countryCodes } = scimUserAggs || {};
      if ((!Array.isArray(departments) || !departments.length) &&
        (!Array.isArray(locations) || !locations.length) &&
        (!Array.isArray(organizations) || !organizations.length) &&
        (!Array.isArray(divisions) || !divisions.length) &&
        (!Array.isArray(titles) || !titles.length) &&
        (!Array.isArray(countryCodes) || !countryCodes.length)) {
        return false;
      }
      return true;
    }
  },
  created() {
    this.state = this.isSimplifiedCampaign ? 'UPLOAD-PARTICIPANTS' : null;
  },
  methods: {
    async saveSegment(segment) {
      try {
        const { campaignID, campaign } = this;
        await this.$apollo.mutate({
          mutation: UpdateCampaignMutation,
          variables: {
            where: { id: campaignID },
            update: {
              segment: {
                title: campaign.title,
                scimUsers: segment,
                status: 'ACTIVE'
              },
            },
          },
        });
        this.$notification({ message: 'Participants are successfully added', variant: 'success' });
        return true;
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Participants could not be added' });
        return false;
      }
    },
    async saveParticipants(participants) {
      try {
        const { campaignID } = this;
        await this.$apollo.mutate({
          mutation: UpdateCampaignMutation,
          variables: {
            where: { id: campaignID },
            update: {
              invitedUsers: participants,
            },
          },
        });
        this.$notification({ message: 'Participants are successfully added', variant: 'success' });
        return true;
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Participants could not be added' });
        return false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
  .draggable-icon {
    font-size: 1.3rem;
  }
  .bg-bar-color {
    background-color: #dee2e6;
  }
  .inactiveButton {
    color: #001DC0;
    text-decoration: underline;
  }
  .activeButton {
    color: white;
    background-color: #001DC0;
  }
  .activeButton:hover {
    background-color: red;
    color: white;
  }
  .inactiveButton:hover {
    color: white;
    background-color: #001DC0;
  }
  .hamburger-icon:hover {
    cursor: move;
  }
  .hidden {
    visibility: hidden;
  }
  .link-styling {
    text-decoration: underline;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/views/campaign-admin/Participants.vue